import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApplicationByIDApi, getMvSorData } from '../api/application';
import { submitReviewNote, checkDuplicates } from "../api/review";
import { handleDecision } from "../actions/review";
import { getFormattedDate } from '../utils/dateUtils';

export const useApplicationReview = (applicationId) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [applicant, setApplicant] = useState(null);
    const [sorData, setSorData] = useState(null);
    const [sorError, setSorError] = useState(null);
    const [logs, setLogs] = useState([]);
    const [duplicateData, setDuplicateData] = useState(null);
    const [isSubmitNoteFailed, setIsSubmitNoteFailed] = useState(null);

    const [isDecisionSubmitFailed, setIsDecisionSubmitFailed] = useState(null);
    const [decisionSubmitError, setDecisionSubmitError] = useState(null);

    const [isAcceptModalVisible, setIsAcceptModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const action = "ADD_REVIEWER_NOTE";
    //const [showReviewExitDialog, setShowReviewExitDialog] = useState(false);

    const navigate = useNavigate();



    const fetchApplicationData = useCallback(async (showLoader = true) => {
        try {
            if (showLoader) {
                setIsLoading(true);
            }
            setError(null);
            const details = await getApplicationByIDApi(applicationId);

            if (!details) {
                throw new Error('Application not found');
            }

            // Fetch SOR data and set the state
            await fetchSorData(applicationId);
            // Fetch duplicates and set the state
            await fetchDuplicates(applicationId);
          
            setApplicant(details?.applicationDetails);
            setLogs(details?.reviews)
        } catch (err) {
            setError(err.message || 'An error occurred while fetching your data. Try again in a few seconds, or contact support if it persists.');
            console.error('Error fetching application:', err);
        } finally {
            setIsLoading(false);
        }
    }, [applicationId]);

    const fetchSorData = useCallback(async (applicationId) => {
        try {
            const data = await getMvSorData(applicationId);
            if (!data) {
                throw new Error('Application not found');
            }
            if (data?.errorMessage != null) {
                if(data?.errorCode == "DECRYPT"){
                    setSorError({
                        message: "Data decryption failed",
                        accessCode: data?.accessCode,
                        isValid:data?.isValid
                    })
                }
                else{
                    setSorError({
                        message:data?.errorMessage
                    });
                }
            }
            else {
                setSorData(data);
            }
        } catch (err) {
            console.error('Error fetching SOR Data:', err);
            console.log("Error response data: ", err.response?.data);
            if (err.response) {
                const status = err.response.status;
                if(status == 400){
                    setSorError({message:"Not Applicable"});
                }
                else if(status == 404){
                    setSorError({message:"SOR Data Not found"})
                }
                else if (status >= 500) {
                    setSorError({message:"Call to fetch SOR Data failed"});
                }
                else {
                    setSorError({message:"An unexpected error occurred"});
                }
            } else {
                setSorError({message:"Call to MV Registry failed"});
            }
        }
    }, [applicationId])

    const fetchDuplicates = useCallback(async (applicationId) => {
        try {
            const data = await checkDuplicates(applicationId);
            if (!data) {
                throw new Error('Application not found');
            }
            setDuplicateData(data);
        } catch (error) {
            console.error('Error fetching duplicates:', error);
            
        }
    }, [applicationId]);

    const submitNote = useCallback(async (noteText) => {
        try {
            if (!applicationId) {
                throw new Error('Application ID is required');
            }

            const user = JSON.parse(sessionStorage.getItem('user'));
            console.log("user: ", user)
            const reviewNoteBody = {
                reviewerID: user?.userId,
                reviewerName: user?.name,
                loa: "",
                notes: noteText
            };

            const res = await submitReviewNote(applicationId, action, reviewNoteBody);
            if (res || res === "") {
                console.log("Review note submitted successfully!")
                await fetchApplicationData(true);
            } else {
                console.warn("Response is not successful. Skipping fetchApplicationData.");
            }
        } catch (error) {
            console.error('Error submitting note:', error);
            console.log("Error response data: ", error.response?.data);
            setIsSubmitNoteFailed(true)
        }
    }, [applicant, fetchApplicationData]);

    const handleApplicationDecision = useCallback(async (decision, loa, notes) => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        console.log("user: ", user)
        try {
            let reviewNote;
            switch (decision) {
                case "APPROVE":
                    reviewNote = "APPROVED"
                    break;
                case "REJECT":
                    reviewNote = "REJECTED : " + notes;
                    break;
                default:
                    reviewNote = ""
                    break;
            }
            const decisionBody = {
                reviewerID: user?.userId,
                reviewerName: user?.name,
                decision,
                loa,
                notes: reviewNote,
            };
            await handleDecision(
                applicationId,
                decisionBody,
                setIsAcceptModalVisible,
                setIsRejectModalVisible,
                navigate
            );
        } catch (error) {
            // Enable the error dialog for both accept and reject error
            setIsDecisionSubmitFailed(true);
            setDecisionSubmitError(error.message || 'Failed to submit the decision. Please try again.');
            console.error('Error submitting decision:', error);
        }
    }, [applicationId, navigate]);

    return {
        isLoading,
        error,
        applicant,
        sorData,
        sorError,
        logs,
        isAcceptModalVisible,
        isRejectModalVisible,
        duplicateData,
        isSubmitNoteFailed,
        isDecisionSubmitFailed,
        decisionSubmitError,
        setDecisionSubmitError,
        setIsDecisionSubmitFailed,
        setIsSubmitNoteFailed,
        setIsAcceptModalVisible,
        setIsRejectModalVisible,
        submitNote,
        handleApplicationDecision,
        fetchApplicationData,
    };
};